/**
 * Ficha da empresa — /empresas/:id.
 *
 * A visão completa que a operação pediu: o cadastro da Receita (com o quadro
 * societário — quem decide), os VALORES de publicação pela tabela oficial
 * vigente da categoria, e a jornada de comunicação inteira (o que já foi
 * enviado, clicado e preenchido). Página, não modal — é material de trabalho
 * de quem vai ligar.
 */

import { nomeDoSegmento } from '@egba-do/shared'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/cliente'
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Carregando,
  Etiqueta,
  Modal,
  Selecao,
  Vazio,
  formatarData,
  formatarDataCurta,
} from '../componentes/ui'

interface Socio {
  nome?: string
  qualificacao?: string
  data_entrada?: string
  faixa_etaria?: string
}

interface Detalhe {
  id: string
  organizacaoId: string | null
  nome: string | null
  razaoSocial: string | null
  cnpj: string | null
  email: string | null
  telefone: string | null
  municipio: string | null
  uf: string | null
  cnaePrincipal: string | null
  segmento: string | null
  porte: string | null
  socios: Socio[] | null
  origem: string
  origemDado: string | null
  snapshotCnpjEm: string | null
  estagio: string
  optinEmail: boolean
  optinWhatsapp: boolean
  criadoEm: string
  categoriaNome: string | null
  tabelaVigente: {
    descricao: string
    regraPreco: { modo?: string; valorUnitarioCentavos?: number }
    vigenciaInicio: string
    vigenciaFim: string | null
    atoAprovacao: string | null
  } | null
  inep: { escola_id: string; nome: string; ano_censo: number } | null
}

interface Jornada {
  envios: Array<{
    id: string
    canal: string
    assunto: string | null
    status: string
    motivoBloqueio: string | null
    criadoEm: string
    enviadoEm: string | null
    clicadoEm: string | null
    convertidoEm: string | null
    campanhaNome: string | null
  }>
  eventos: Array<{
    id: string
    tipo: string
    canal: string | null
    ocorridoEm: string
    landingTitulo: string | null
  }>
}

const ESTAGIOS = [
  { valor: 'novo', texto: 'Nova' },
  { valor: 'qualificado', texto: 'Qualificada' },
  { valor: 'em_negociacao', texto: 'Em negociação' },
  { valor: 'ganho', texto: 'Ganha' },
  { valor: 'perdido', texto: 'Perdida' },
]

const NOME_EVENTO: Record<string, string> = {
  visita: 'Visitou a landing page',
  submissao: 'Preencheu o formulário',
  clique: 'Clicou',
  abertura: 'Abriu e-mail',
}

const brl = (c: number): string =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

function cnpjFmt(v: string | null): string {
  if (!v || v.length !== 14) return v ?? '—'
  return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-slate-500">{rotulo}</span>
      <span className="min-w-0 text-right text-slate-800">{children}</span>
    </div>
  )
}

export function Empresa() {
  const { id } = useParams<{ id: string }>()
  const navegar = useNavigate()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['empresa', id],
    queryFn: () => api.get<{ dados: Detalhe }>(`/api/v1/leads/${id}`),
    retry: 1,
  })

  const { data: jornada } = useQuery({
    queryKey: ['empresa-jornada', id],
    queryFn: () => api.get<{ dados: Jornada }>(`/api/v1/leads/${id}/jornada`),
  })

  const [convertendo, setConvertendo] = useState(false)
  const [convidar, setConvidar] = useState(true)
  const [convite, setConvite] = useState({ nome: '', email: '' })
  const [resultadoConvite, setResultadoConvite] = useState<{
    email: string
    senhaProvisoria: string
    jaExistia: boolean
  } | null>(null)
  const [erroConversao, setErroConversao] = useState<string | null>(null)

  const converter = useMutation({
    mutationFn: () =>
      api.post<{
        dados: {
          organizacaoId: string
          organizacaoCriada: boolean
          convite: { email: string; senhaProvisoria: string; jaExistia: boolean } | null
        }
      }>(`/api/v1/leads/${id}/converter`, {
        ...(convidar && convite.email ? { convidar } : {}),
      }),
    onSuccess: (r) => {
      setErroConversao(null)
      setResultadoConvite(r.dados.convite)
      // Sem convite não há senha para mostrar — o modal pode fechar.
      if (!r.dados.convite) setConvertendo(false)
      void qc.invalidateQueries({ queryKey: ['empresa', id] })
      void qc.invalidateQueries({ queryKey: ['empresas'] })
    },
    onError: (e: Error) => setErroConversao(e.message),
  })

  const mudarEstagio = useMutation({
    mutationFn: (novo: string) => api.put(`/api/v1/leads/${id}/estagio`, { estagio: novo }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['empresa', id] })
      void qc.invalidateQueries({ queryKey: ['empresas'] })
    },
  })

  if (isLoading) return <Carregando />

  const e = data?.dados
  if (!e) {
    return (
      <Vazio
        titulo="Empresa não encontrada"
        descricao={(error as Error | null)?.message ?? 'Ela pode ter sido removida da base.'}
        acao={<Botao onClick={() => void navegar('/empresas')}>Ver as empresas</Botao>}
      />
    )
  }

  const socios = Array.isArray(e.socios) ? e.socios : []
  const regra = e.tabelaVigente?.regraPreco
  const j = jornada?.dados

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------- cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/empresas" className="mb-1 block text-sm text-slate-500 hover:text-slate-900">
            ← Empresas
          </Link>
          <h2 className="text-xl font-semibold text-slate-900">
            {e.razaoSocial ?? e.nome ?? 'Empresa'}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <Etiqueta tom={e.segmento === 'empresa_balanco' ? 'marca' : 'sucesso'}>
              {nomeDoSegmento(e.segmento) ?? 'Sem segmento'}
            </Etiqueta>
            <span className="font-mono">{cnpjFmt(e.cnpj)}</span>
            {e.municipio && (
              <span>
                {e.municipio}/{e.uf}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-end gap-3">
          {e.organizacaoId ? (
            <Link to="/instituicoes">
              <Botao variante="secundario">Cliente — ver instituição</Botao>
            </Link>
          ) : (
            <Botao
              onClick={() => {
                // Pré-preenche o convite com o melhor contato que temos: o
                // primeiro sócio nominal e o e-mail do cadastro.
                setConvite({
                  nome: socios[0]?.nome ?? e.razaoSocial ?? '',
                  email: e.email ?? '',
                })
                setResultadoConvite(null)
                setErroConversao(null)
                setConvertendo(true)
              }}
            >
              Tornar cliente
            </Botao>
          )}
          <div className="w-48">
            <Selecao
              rotulo="Situação comercial"
              value={e.estagio}
              onChange={(ev) => mudarEstagio.mutate(ev.target.value)}
              opcoes={ESTAGIOS}
            />
          </div>
        </div>
      </div>

      {/* -------------------------------------------------- tornar cliente */}
      <Modal
        aberto={convertendo}
        aoFechar={() => setConvertendo(false)}
        titulo="Tornar cliente"
        descricao={e.razaoSocial ?? undefined}
      >
        {resultadoConvite ? (
          <div className="space-y-4">
            <Aviso tom="sucesso" titulo="Conta aberta">
              {resultadoConvite.jaExistia
                ? `${resultadoConvite.email} já tinha login — o acesso à instituição foi concedido.`
                : `Acesso criado para ${resultadoConvite.email}.`}
            </Aviso>
            {!resultadoConvite.jaExistia && (
              <div className="rounded-xl border border-[color:var(--color-borda)] bg-slate-50 p-4 text-center">
                <p className="text-xs text-slate-500">Senha provisória — entregue por canal seguro</p>
                <p className="mt-1 font-mono text-2xl font-bold text-slate-900 select-all">
                  {resultadoConvite.senhaProvisoria}
                </p>
              </div>
            )}
            <div className="flex justify-end">
              <Botao onClick={() => setConvertendo(false)}>Fechar</Botao>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {erroConversao && <Aviso tom="perigo">{erroConversao}</Aviso>}
            <p className="text-sm leading-relaxed text-slate-600">
              Cria a instituição com o cadastro da Receita — CNPJ, endereço e quadro societário —
              pronta para receber pedidos de publicação. O histórico de captação continua nesta
              ficha.
            </p>
            <label className="flex items-start gap-2.5 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={convidar}
                onChange={(ev) => setConvidar(ev.target.checked)}
              />
              <span>
                Criar também o primeiro acesso de cliente
                <span className="block text-xs text-slate-500">
                  A pessoa entra no painel, envia os atos e aprova valores. A senha provisória
                  aparece aqui — nada é enviado por e-mail automaticamente.
                </span>
              </span>
            </label>
            {convidar && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  rotulo="Nome de quem acessa"
                  value={convite.nome}
                  onChange={(ev) => setConvite({ ...convite, nome: ev.target.value })}
                />
                <Campo
                  rotulo="E-mail de acesso"
                  type="email"
                  value={convite.email}
                  onChange={(ev) => setConvite({ ...convite, email: ev.target.value })}
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Botao variante="secundario" onClick={() => setConvertendo(false)}>
                Cancelar
              </Botao>
              <Botao
                carregando={converter.isPending}
                disabled={convidar && (!convite.email.includes('@') || convite.nome.trim().length < 3)}
                onClick={() => converter.mutate()}
              >
                Abrir a conta
              </Botao>
            </div>
          </div>
        )}
      </Modal>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ------------------------------------------------------- cadastro */}
        <Cartao titulo="Cadastro (Receita Federal)">
          <div className="divide-y divide-[color:var(--color-borda-suave)]">
            <Linha rotulo="Razão social">{e.razaoSocial ?? '—'}</Linha>
            <Linha rotulo="CNPJ">
              <span className="font-mono">{cnpjFmt(e.cnpj)}</span>
            </Linha>
            <Linha rotulo="Porte">{e.porte ?? '—'}</Linha>
            <Linha rotulo="CNAE principal">{e.cnaePrincipal ?? '—'}</Linha>
            <Linha rotulo="E-mail">{e.email ?? '—'}</Linha>
            <Linha rotulo="Telefone">{e.telefone ?? '—'}</Linha>
            <Linha rotulo="Autorizações">
              {[e.optinEmail ? 'e-mail' : null, e.optinWhatsapp ? 'WhatsApp' : null]
                .filter(Boolean)
                .join(' · ') || 'nenhuma'}
            </Linha>
            <Linha rotulo="Na base desde">
              {formatarData(e.criadoEm)}
              {e.snapshotCnpjEm ? ` · retrato de ${formatarDataCurta(e.snapshotCnpjEm)}` : ''}
            </Linha>
          </div>
          {e.inep && (
            <Aviso tom="info" titulo="Também é escola no Censo">
              {e.inep.nome} (Censo {e.inep.ano_censo}).{' '}
              <Link to={`/mercado/escola/${e.inep.escola_id}`} className="font-medium">
                Abrir a ficha do mercado
              </Link>
            </Aviso>
          )}
        </Cartao>

        {/* ------------------------------------------------ quadro societário */}
        <Cartao
          titulo="Quadro societário"
          descricao="Quem decide — da base pública da Receita. É por aqui que a conversa avança."
        >
          {socios.length === 0 ? (
            <p className="text-sm text-slate-400">
              Sem sócios na base pública (comum em matriz/filial ou MEI).
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--color-borda-suave)]">
              {socios.slice(0, 8).map((s, i) => (
                <li key={i} className="py-2.5">
                  <p className="text-sm font-medium text-slate-800">{s.nome ?? '—'}</p>
                  <p className="text-xs text-slate-500">
                    {[s.qualificacao, s.data_entrada ? `desde ${formatarDataCurta(s.data_entrada)}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </li>
              ))}
              {socios.length > 8 && (
                <li className="pt-2 text-xs text-slate-400">e mais {socios.length - 8}…</li>
              )}
            </ul>
          )}
        </Cartao>

        {/* --------------------------------------------- valores de publicação */}
        <Cartao
          titulo="Valores de publicação"
          descricao={e.categoriaNome ? `Categoria: ${e.categoriaNome}` : 'Sem categoria definida'}
        >
          {e.tabelaVigente && regra?.valorUnitarioCentavos ? (
            <div className="space-y-3">
              <div className="rounded-xl bg-marca-50 p-4 text-center">
                <p className="text-3xl font-bold text-marca-800 tabular">
                  {brl(regra.valorUnitarioCentavos)}
                </p>
                <p className="mt-1 text-sm text-marca-700">
                  por centímetro de coluna
                  {regra.modo && regra.modo !== 'cm_coluna' ? ` (${regra.modo})` : ''}
                </p>
              </div>
              <div className="divide-y divide-[color:var(--color-borda-suave)]">
                <Linha rotulo="Tabela">{e.tabelaVigente.descricao}</Linha>
                <Linha rotulo="Vigência">
                  desde {formatarDataCurta(e.tabelaVigente.vigenciaInicio)}
                  {e.tabelaVigente.vigenciaFim
                    ? ` até ${formatarDataCurta(e.tabelaVigente.vigenciaFim)}`
                    : ''}
                </Linha>
                {e.tabelaVigente.atoAprovacao && (
                  <Linha rotulo="Ato">{e.tabelaVigente.atoAprovacao}</Linha>
                )}
              </div>
              <p className="text-xs leading-relaxed text-slate-400">
                O valor final depende do tamanho diagramado do ato — o orçamento exato sai antes de
                qualquer publicação, para aprovação da empresa.
              </p>
            </div>
          ) : (
            <Aviso tom="atencao">
              Sem tabela de preço vigente para a categoria — cadastre em Tabelas de preço.
            </Aviso>
          )}
        </Cartao>
      </div>

      {/* -------------------------------------------------------------- jornada */}
      <Cartao
        titulo="Jornada de comunicação"
        descricao="Tudo que já aconteceu com esta empresa — envios, cliques, visitas e formulários."
      >
        {!j || (j.envios.length === 0 && j.eventos.length === 0) ? (
          <Vazio
            titulo="Nenhum contato ainda"
            descricao="Quando uma campanha alcançar esta empresa — ou ela visitar uma landing — o histórico aparece aqui."
          />
        ) : (
          <ul className="space-y-3">
            {[
              ...j.envios.map((v) => ({
                quando: v.enviadoEm ?? v.criadoEm,
                no: (
                  <li key={`e${v.id}`} className="flex gap-4">
                    <span
                      aria-hidden="true"
                      className={`mt-1.5 block h-2.5 w-2.5 shrink-0 rounded-full ${
                        v.status === 'bloqueado' || v.status === 'falhou'
                          ? 'bg-amber-400'
                          : 'bg-marca-500'
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800">
                        <span className="capitalize">{v.canal}</span>
                        {v.assunto ? ` — ${v.assunto}` : ''}
                      </p>
                      <p className="text-xs text-slate-500">
                        {v.campanhaNome ?? 'envio avulso'} · {v.status}
                        {v.motivoBloqueio ? ` (${v.motivoBloqueio})` : ''}
                        {v.clicadoEm ? ' · clicou' : ''}
                        {v.convertidoEm ? ' · preencheu' : ''} ·{' '}
                        {formatarData(v.enviadoEm ?? v.criadoEm)}
                      </p>
                    </div>
                  </li>
                ),
              })),
              ...j.eventos.map((v) => ({
                quando: v.ocorridoEm,
                no: (
                  <li key={`v${v.id}`} className="flex gap-4">
                    <span
                      aria-hidden="true"
                      className="mt-1.5 block h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-500"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800">{NOME_EVENTO[v.tipo] ?? v.tipo}</p>
                      <p className="text-xs text-slate-500">
                        {v.landingTitulo ?? 'landing page'} · {formatarData(v.ocorridoEm)}
                      </p>
                    </div>
                  </li>
                ),
              })),
            ]
              .sort((a, b) => (a.quando < b.quando ? 1 : -1))
              .map((x) => x.no)}
          </ul>
        )}
      </Cartao>
    </div>
  )
}
